// ABOUTME: TypeScript types for the Order Change Lead Time configuration feature.
// ABOUTME: Covers API response/request shapes, form values, override tracking, and field type enums.

import {MetaResponse, OverrideValue} from '.'
import {UomRef} from './orderIntakeLeadTime'

// --- Enums ---

/** The type value from the API — camelCase string */
export type OrderChangeFieldType = 'notChangeable' | 'autoApproval' | 'slotManagementApproval'

// --- API response types ---

export interface OrderChangeLeadTimeValueResponse {
  value: number
  uom: UomRef
  referenceEvent: string
}

export interface OrderChangeFieldConfigResponse {
  id: string
  fieldCode: string
  type: OverrideValue<OrderChangeFieldType> | null
  autoApproveLeadTime: OverrideValue<OrderChangeLeadTimeValueResponse | null> | null
  orderManagementLeadTime: OverrideValue<OrderChangeLeadTimeValueResponse | null> | null
  _meta: MetaResponse
}

export interface OrderChangeScopeResponse {
  scopeId: string
  configurations: OrderChangeFieldConfigResponse[]
}

// --- API request types ---

export interface OrderChangeLeadTimeValueRequest {
  value: number
  uomId: string
  referenceEventId: number
}

export interface OrderChangeFieldConfigRequest {
  fieldCode: string
  type: OverrideValue<string>
  autoApproveLeadTime: OverrideValue<OrderChangeLeadTimeValueRequest | null>
  orderManagementLeadTime: OverrideValue<OrderChangeLeadTimeValueRequest | null>
}

export interface PutOrderChangeLeadTimeRequest {
  configurations: OrderChangeFieldConfigRequest[]
}

// --- Form-ready types ---

export interface LeadTimeFormValue {
  value: number
  uomId: string
  uomDisplay: string
  referenceEvent: string
}

export interface OrderChangeFieldFormValues {
  fieldCode: string
  type: OrderChangeFieldType
  autoApproveLeadTime: LeadTimeFormValue
  orderManagementLeadTime: LeadTimeFormValue
}

export interface OrderChangeLeadTimeFormValues {
  configurations: OrderChangeFieldFormValues[]
}

// --- Config (query result) ---

export interface OrderChangeLeadTimeConfig {
  id: string | undefined
  configurations: OrderChangeFieldFormValues[]
  overrides: {configurations: boolean}
  parentValues: OrderChangeLeadTimeFormValues
  _meta: {
    scopeId: string
    isInherited: boolean
    createdAt: string
    updatedAt: string
    createdBy: string
    updatedBy: string
  }
}

// --- Constants ---

/** Reference event mapping: string → numeric ID (for API requests) */
export const REFERENCE_EVENT_TO_ID: Record<string, number> = {
  beforePrePlanning: 1,
  beforeRequestedDeliveryTime: 2,
  afterFirstTruckLoaded: 3,
  afterMinQuantityReached: 4,
  afterOrderCompleted: 5,
  afterOrderTicketed: 6,
}

export const REFERENCE_EVENT_FROM_ID: Record<number, string> = Object.fromEntries(
  Object.entries(REFERENCE_EVENT_TO_ID).map(([k, v]) => [v, k])
)

/** Maps PascalCase field codes (from migrated data) to canonical camelCase keys */
const FIELD_CODE_ALIASES: Record<string, string> = {
  OrderConfirmation: 'orderConfirmation',
  MaterialChange: 'materialChange',
  Quantity: 'quantity',
  PlusLoad: 'plusLoad',
  MaxLoadSize: 'maxLoadSize',
  DeliveryRate: 'deliveryRate',
  FlowRate: 'flowRate',
  TimeOnJobSite: 'timeOnJobSite',
  DeliveryDate: 'deliveryDate',
  EarliestDeliveryTime: 'earliestDeliveryTime',
  LatestDeliveryTime: 'latestDeliveryTime',
  IsTestBooked: 'isTestBooked',
  SiteContact: 'siteContact',
  PoNumber: 'poNumber',
  ChangeNotes: 'changeNotes',
}

/** Normalize a field code to the canonical camelCase form used in translation keys */
export const normalizeFieldCode = (fieldCode: string): string =>
  FIELD_CODE_ALIASES[fieldCode] ?? fieldCode.charAt(0).toLowerCase() + fieldCode.slice(1)

// --- Transformers ---

export const DEFAULT_LEAD_TIME: LeadTimeFormValue = {
  value: 0,
  uomId: 'h',
  uomDisplay: 'hours',
  referenceEvent: 'beforeRequestedDeliveryTime',
}

/** Transform a single API field config to form-ready values */
export const toFormValues = (config: OrderChangeFieldConfigResponse): OrderChangeFieldFormValues => ({
  fieldCode: config.fieldCode,
  type: config.type?.value ?? 'notChangeable',
  autoApproveLeadTime: config.autoApproveLeadTime?.value
    ? {
        value: config.autoApproveLeadTime.value.value,
        uomId: config.autoApproveLeadTime.value.uom.id,
        uomDisplay: config.autoApproveLeadTime.value.uom.displaySymbol,
        referenceEvent: config.autoApproveLeadTime.value.referenceEvent,
      }
    : {...DEFAULT_LEAD_TIME},
  orderManagementLeadTime: config.orderManagementLeadTime?.value
    ? {
        value: config.orderManagementLeadTime.value.value,
        uomId: config.orderManagementLeadTime.value.uom.id,
        uomDisplay: config.orderManagementLeadTime.value.uom.displaySymbol,
        referenceEvent: config.orderManagementLeadTime.value.referenceEvent,
      }
    : {...DEFAULT_LEAD_TIME},
})

/** Transform a full scope response to config shape */
export const toConfig = (response: OrderChangeScopeResponse, parentValues?: OrderChangeLeadTimeFormValues): OrderChangeLeadTimeConfig => {
  const firstConfig = response.configurations[0]
  const meta = firstConfig?._meta
  const configurations = response.configurations.map(toFormValues)
  return {
    id: firstConfig?.id,
    configurations,
    overrides: {configurations: true},
    parentValues: parentValues ?? {configurations},
    _meta: {
      scopeId: response.scopeId,
      isInherited: false,
      createdAt: meta?.createdAt ?? '',
      updatedAt: meta?.updatedAt ?? '',
      createdBy: meta?.createdBy ?? '',
      updatedBy: meta?.updatedBy ?? '',
    },
  }
}
